class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.onUpsellAddClick.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (!containerToTrapFocusOn || typeof trapFocus !== 'function') return;
        trapFocus(containerToTrapFocusOn, focusElement || containerToTrapFocusOn);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }


  onUpsellAddClick(event) {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.addUpsellItem(button, variantId);
  }

  async addUpsellItem(button, variantId) {
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');
    if (errorContainer) errorContainer.textContent = '';

    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    this.setActiveElement(button);

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedState = await response.json();

      if (parsedState.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          errors: parsedState.errors || parsedState.description,
          message: parsedState.message,
        });

        if (errorContainer) {
          errorContainer.textContent = parsedState.description || parsedState.message || 'Unable to add item to cart.';
        }

        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-upsell',
        productVariantId: variantId,
        cartData: parsedState,
      });

      this.renderContents(parsedState);
    } catch (error) {
      console.error(error);
      if (errorContainer) errorContainer.textContent = 'Unable to add item to cart.';
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }
  async ensureDrawerSectionHasItems(parsedState) {
    if (!parsedState || !parsedState.sections) return parsedState;
    const itemCount = Number(parsedState.item_count || 0);
    const drawerSectionHtml = parsedState.sections['cart-drawer'];
    if (!itemCount || !drawerSectionHtml) return parsedState;

    const parsedDrawerDoc = new DOMParser().parseFromString(drawerSectionHtml, 'text/html');
    const hasCartItems = !!parsedDrawerDoc.querySelector('.cart-item');
    if (hasCartItems) return parsedState;

    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const html = await response.text();
      const parsedFallbackDoc = new DOMParser().parseFromString(html, 'text/html');
      const fallbackHasItems = !!parsedFallbackDoc.querySelector('.cart-item');
      if (!parsedState.sections) parsedState.sections = {};
      parsedState.sections['cart-drawer'] = html;
    } catch (error) {
      // Keep original payload if fallback fetch fails.
    }

    return parsedState;
  }

  async renderContents(parsedState) {
    const normalizedState = await this.ensureDrawerSectionHasItems(parsedState);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    const inferredItemCountFromArray = Array.isArray(normalizedState && normalizedState.items)
      ? normalizedState.items.length
      : null;
    const parsedItemCount = Number(normalizedState && normalizedState.item_count);
    const hasFiniteItemCount = Number.isFinite(parsedItemCount);
    const drawerSectionHtml =
      normalizedState && normalizedState.sections ? normalizedState.sections['cart-drawer'] : '';
    const drawerSectionHasItems = String(drawerSectionHtml || '').includes('class="cart-item') || String(drawerSectionHtml || '').includes("class='cart-item");
    const hasItems = hasFiniteItemCount
      ? parsedItemCount > 0
      : (inferredItemCountFromArray !== null ? inferredItemCountFromArray > 0 : drawerSectionHasItems);
    this.classList.toggle('is-empty', !hasItems);

    const drawerItemsElement = this.querySelector('cart-drawer-items');
    if (drawerItemsElement) {
      drawerItemsElement.classList.toggle('is-empty', !hasItems);
    }

    this.productId = normalizedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(normalizedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

